import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Typography, Paper, Grid, TextField, MenuItem, Button,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Divider, Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = '/api/contabilidad';
function fmt(v) { return parseFloat(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtMes(p) {
  if (!p || p.length !== 6) return p;
  const mn = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  return mn[parseInt(p.substring(4,6))-1];
}

const AÑOS = Array.from({ length: 6 }, (_, i) => String(new Date().getFullYear() - i));

export default function Pdt0621() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [empresa, setEmpresa] = useState(null);
  const [anio, setAnio] = useState(String(new Date().getFullYear()));
  const [datos, setDatos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/empresas`).then(r => setEmpresas(r.data)).catch(() => {});
  }, []);

  async function generar() {
    if (!empresaId) { setError('Selecciona una empresa'); return; }
    setLoading(true); setError('');
    try {
      const emp = empresas.find(e => e.id == empresaId);
      setEmpresa(emp);
      const r = await axios.get(`${API}/pdt0621?empresa_id=${empresaId}&anio=${anio}`);
      setDatos(r.data.datos || []);
    } catch (e) { setError(e.response?.data?.error || 'Error al calcular'); }
    setLoading(false);
  }

  const totalVentasBase = datos.reduce((s, d) => s + d.ventas_base, 0);
  const totalVentasIgv  = datos.reduce((s, d) => s + d.ventas_igv, 0);
  const totalComprasBase = datos.reduce((s, d) => s + d.compras_base, 0);
  const totalCf         = datos.reduce((s, d) => s + d.compras_cf, 0);
  const totalIgvPagar   = datos.reduce((s, d) => s + d.igv_pagar, 0);
  const totalRenta      = datos.reduce((s, d) => s + d.renta_pago_cuenta, 0);
  const totalRetenc     = datos.reduce((s, d) => s + d.retenciones, 0);

  function exportarExcel() {
    const headers = ['Período','Mes','Base Grav. Ventas','IGV Ventas','Base Grav. Compras','Crédito Fiscal','IGV a Pagar','Renta 3ra (1.5%)','Retenciones Dep.'];
    const rows = datos.map(d => [
      d.periodo, fmtMes(d.periodo),
      d.ventas_base, d.ventas_igv, d.compras_base, d.compras_cf,
      d.igv_pagar, d.renta_pago_cuenta, d.retenciones
    ]);
    rows.push(['TOTAL','', totalVentasBase, totalVentasIgv, totalComprasBase, totalCf, totalIgvPagar, totalRenta, totalRetenc]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = headers.map(() => ({ wch: 18 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `PDT0621-${anio}`);
    XLSX.writeFile(wb, `PDT0621_${empresa?.ruc || empresaId}_${anio}.xlsx`);
  }

  function exportarPdf() {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(14);
    doc.text(`PDT 0621 — IGV y Renta Mensual — ${anio}`, 14, 15);
    doc.setFontSize(10);
    doc.text(`${empresa?.razon_social || ''} | RUC: ${empresa?.ruc || ''}`, 14, 22);
    autoTable(doc, {
      startY: 28,
      head: [['Período','Base Ventas','IGV Ventas','Base Compras','Créd. Fiscal','IGV a Pagar','Renta 1.5%','Retenciones']],
      body: [
        ...datos.map(d => [
          `${fmtMes(d.periodo)} ${anio}`,
          fmt(d.ventas_base), fmt(d.ventas_igv),
          fmt(d.compras_base), fmt(d.compras_cf),
          fmt(d.igv_pagar), fmt(d.renta_pago_cuenta), fmt(d.retenciones)
        ]),
        ['TOTAL', fmt(totalVentasBase), fmt(totalVentasIgv), fmt(totalComprasBase),
         fmt(totalCf), fmt(totalIgvPagar), fmt(totalRenta), fmt(totalRetenc)]
      ],
      styles: { fontSize: 8 },
      headStyles: { fillColor: [25, 118, 210] },
      footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold' }
    });
    doc.save(`PDT0621_${empresa?.ruc || empresaId}_${anio}.pdf`);
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <ReceiptLongIcon color="primary" />
        <Box>
          <Typography variant="h5" fontWeight="bold">PDT 0621 — IGV y Renta Mensual</Typography>
          <Typography variant="body2" color="text.secondary">
            Resumen mensual de ventas, compras, IGV a pagar y pagos a cuenta de Renta 3ra Categoría
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Empresa" value={empresaId}
              onChange={e => { setEmpresaId(e.target.value); setDatos([]); }} sx={{ minWidth: '24em' }}>
              {empresas.map(e => <MenuItem key={e.id} value={e.id}>{e.razon_social}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Año" value={anio}
              onChange={e => { setAnio(e.target.value); setDatos([]); }} sx={{ minWidth: '10em' }}>
              {AÑOS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm="auto">
            <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              onClick={generar} disabled={loading || !empresaId}>
              Calcular
            </Button>
          </Grid>
          {datos.length > 0 && <>
            <Grid item xs={12} sm="auto">
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportarExcel} color="success">
                Excel
              </Button>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={exportarPdf} color="error">
                PDF
              </Button>
            </Grid>
          </>}
        </Grid>
      </Paper>

      {loading ? <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} /> : datos.length > 0 && (
        <>
          {/* Resumen anual cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total Ventas Gravadas', value: totalVentasBase, color: '#1976d2' },
              { label: 'Total IGV Ventas', value: totalVentasIgv, color: '#388e3c' },
              { label: 'Crédito Fiscal Total', value: totalCf, color: '#e65100' },
              { label: 'IGV a Pagar Total', value: totalIgvPagar, color: '#d32f2f' },
              { label: 'Renta 3ra (1.5%) Total', value: totalRenta, color: '#7b1fa2' },
              { label: 'Retenciones Depositadas', value: totalRetenc, color: '#0288d1' },
            ].map((k, i) => (
              <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
                <Paper sx={{ p: 2, borderRadius: 2, borderTop: `4px solid ${k.color}`, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" display="block">{k.label}</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: k.color }}>S/ {fmt(k.value)}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Cabecera empresa */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" align="center">{empresa?.razon_social}</Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              RUC: {empresa?.ruc} — Período Tributario: Enero — Diciembre {anio}
            </Typography>
            <Typography variant="caption" color="warning.main" align="center" display="block" sx={{ mt: 0.5 }}>
              * La Renta 3ra (1.5%) es un estimado de pago a cuenta. El coeficiente real lo determina la DJ Anual.
            </Typography>
          </Box>

          <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Período</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                    <Tooltip title="Base imponible ventas gravadas con IGV"><span>Base Ventas</span></Tooltip>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">IGV Ventas</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                    <Tooltip title="Base imponible compras gravadas"><span>Base Compras</span></Tooltip>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Crédito Fiscal</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', bgcolor: '#d32f2f' }} align="right">IGV a Pagar</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', bgcolor: '#7b1fa2' }} align="right">Renta 1.5%</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Retenciones</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Docs V/C</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {datos.map((d, i) => (
                  <TableRow key={i} hover sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafafa' } }}>
                    <TableCell>
                      <Typography fontWeight="bold" fontSize="0.85rem">{fmtMes(d.periodo)}</Typography>
                      <Typography variant="caption" color="text.secondary">{d.periodo}</Typography>
                    </TableCell>
                    <TableCell align="right">{fmt(d.ventas_base)}</TableCell>
                    <TableCell align="right" sx={{ color: '#388e3c', fontWeight: 'bold' }}>{fmt(d.ventas_igv)}</TableCell>
                    <TableCell align="right">{fmt(d.compras_base)}</TableCell>
                    <TableCell align="right" sx={{ color: '#e65100' }}>{fmt(d.compras_cf)}</TableCell>
                    <TableCell align="right">
                      <Chip label={`S/ ${fmt(d.igv_pagar)}`} size="small"
                        color={d.igv_pagar > 0 ? 'error' : 'default'}
                        sx={{ fontWeight: 'bold', minWidth: 100 }} />
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={`S/ ${fmt(d.renta_pago_cuenta)}`} size="small"
                        sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2', fontWeight: 'bold', minWidth: 100 }} />
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#0288d1' }}>{fmt(d.retenciones)}</TableCell>
                    <TableCell align="center">
                      <Typography variant="caption">{d.ventas_docs}V / {d.compras_docs}C</Typography>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: '#e3f2fd', fontWeight: 'bold' }}>
                  <TableCell><Typography fontWeight="bold">TOTAL ANUAL</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">{fmt(totalVentasBase)}</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold" color="success.main">{fmt(totalVentasIgv)}</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">{fmt(totalComprasBase)}</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold" color="warning.main">{fmt(totalCf)}</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold" color="error.main">{fmt(totalIgvPagar)}</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold" sx={{ color: '#7b1fa2' }}>{fmt(totalRenta)}</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold" color="info.main">{fmt(totalRetenc)}</Typography></TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 3 }} />
          <Paper sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
            <Typography variant="body2" fontWeight="bold" color="warning.dark" mb={1}>Notas importantes:</Typography>
            <Typography variant="body2" color="text.secondary">
              • <b>IGV a Pagar</b> = IGV Ventas − Crédito Fiscal Compras (mínimo S/ 0.00)<br/>
              • <b>Renta 3ra Categoría (1.5%)</b>: Pago a cuenta estimado sobre ventas totales. El coeficiente definitivo lo establece la Declaración Jurada Anual (DJ Anual IR).<br/>
              • <b>Retenciones Depositadas</b>: Solo incluye retenciones IGV marcadas como DEPOSITADAS.<br/>
              • Esta pantalla es de referencia. Para declarar, use el Formulario Virtual PDT 0621 en SUNAT Operaciones en Línea.
            </Typography>
          </Paper>
        </>
      )}
    </Box>
  );
}
